import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Form } from 'src/core/forms';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useQuery } from '@apollo/client';
import { AppendState as AppendCartState } from 'src/core/state/cart';
import { Messages } from 'src/components/Messages/Messages';
import { errorMessage } from 'src/core/engine/utils/general';
import { Block } from 'src/components/Helpers/Block';
import {
    getCustomisationLocations,
    getCustomisationLocations_products_items_locations,
    getCustomisationLocationsVariables,
} from 'src/queries/__generated__/getCustomisationLocations';
import { PriceSummary } from 'src/components/CustomisationPage/utils/priceEstimates';
import { SummaryTable } from 'src/components/CustomisationPage/SummaryTable';
import { Breakpoint, useBreakpoint } from 'src/hooks/useBreakpoint';
import { useSearch } from 'src/hooks/router-hooks';
import { CustomizationSkeleton } from 'src/elements/Skeletons/Skeletons';

import { Actions, ErrorSummaryText } from '../../Forms/Actions';
import { InfoBlock } from '../InfoBlock';
import { useCustomisation } from '../hooks/useCustomisation';
import {
    ApplicationMethod,
    ApplicationMethodAPI,
    ProductLocation,
    Step,
    methodFromLocations,
} from '../feature/customisation.types';
import getCustomisationLocationsQuery from '../../../queries/getCustomisationLocations.graphql';
import { ErrorOrOfflineConnected } from '../../Error/ErrorOrOffline';
import { cleanLocations } from '../utils/cleanLocations';
import { AppendState, CustomisationMsg } from '../feature/customisation.actions';
import { ErrorText } from '../../Forms/ErrorFor';
import { PositionFieldset } from '../steps/PositionFieldSet';

import classes from './Pages.scss';

type Props = {
    locations: ProductLocation[];
    locationNames: string[];
    summary: PriceSummary | null;
    takenLocations: string[];
    recommendedApplication: number;
};

export const Position: React.FC<Props> = React.memo(({ locations, locationNames, summary, takenLocations }) => {
    const initialValues = locationNames.reduce((acc, name) => {
        acc[name] = true;
        return acc;
    }, {});
    const dispatch = useDispatch();
    const [error, setError] = useState<string | undefined>(undefined);
    const [selectedNr, setSelectedNr] = useState<number>(0);
    const [breakpoint] = useBreakpoint();

    const selectedApplicationMethod = (locationNames: string[]) => {
        if (!locations) {
            return ApplicationMethod.Both;
        }
        return methodFromLocations(locations, locationNames);
    };

    const manageChange = (event) => {
        setSelectedNr(Object.keys(event).filter((value) => event[value] !== false).length);
    };

    const submitPosition = useCallback(
        (values) => {
            const keys = Object.keys(values);
            const filtered = keys.filter((key) => values[key] === true);
            if (filtered.length === 0) {
                return setError('Please select at least 1 position');
            }
            dispatch(CustomisationMsg('Customisation.PositionSubmit', filtered));
            if (selectedApplicationMethod(filtered) !== ApplicationMethod.Both) {
                const payload = {
                    applicationMethod: selectedApplicationMethod(filtered),
                    oneMethodAvailable: true,
                };
                dispatch(CustomisationMsg('Customisation.MethodSubmit', payload));
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [dispatch],
    );

    const onlyLocationsNames = locations.map((el) => el.name);
    const onlyInitialValueNames = Object.keys(initialValues).map((key) => key);
    const locationsWhichAreNotInitiallyChosen = onlyLocationsNames.filter(
        (obj) => onlyInitialValueNames.indexOf(obj) === -1,
    );
    const locationsWhichAreUsedInOtherLogo = locationsWhichAreNotInitiallyChosen.filter(
        (obj) => takenLocations.indexOf(obj) !== -1,
    );

    return (
        <div className={classes.fadein}>
            <h2 className={classes.header}>1. Choose position(s)</h2>
            {locations.length === 0 && (
                <Block>
                    <Messages messages={[errorMessage('No positions are available for this product')]} />
                </Block>
            )}
            {locations.length > 0 && (
                <Form
                    className={classes.form}
                    onSubmit={submitPosition}
                    initialValues={initialValues}
                    onValueChange={manageChange}
                >
                    <PositionFieldset
                        locations={locations}
                        alreadyUsedLocations={locationsWhichAreUsedInOtherLogo}
                        selectedNr={selectedNr}
                    />
                    {error && <ErrorText text={error} />}
                    {breakpoint === Breakpoint.Mob && (
                        <div>
                            {summary && <SummaryTable summary={summary} />}
                            {!summary && (
                                <p style={{ marginTop: '20px', marginBottom: '20px' }}>
                                    Prices will show here once you've completed the customisation
                                </p>
                            )}
                        </div>
                    )}
                    <Actions
                        primaryText="Continue"
                        direction="reverse"
                        variant="space-between"
                        selectedNr={selectedNr}
                    />
                    {error && <ErrorSummaryText count={1} />}
                </Form>
            )}
            <InfoBlock />
        </div>
    );
});

export const PagePositionConnected: React.FC = React.memo(() => {
    const {
        loadStep,
        isPending,
        state: { locationNames, takenLocations },
    } = useCustomisation();
    const search = useSearch();

    useEffect(() => {
        /**
         * Don't dispatch LoadStep without a valid `?input=...` search string —
         * the epic would fail to parse it and bounce the user to the basket.
         * This happens because after Continue, the redux router updates the
         * search to "" before this component unmounts, briefly re-running this
         * effect with an empty search.
         *
         * Also clean up the timeout on unmount so a transitional re-render
         * can't dispatch a stale LoadStep that races the new page's epic.
         */
        if (!search) return;
        const id = window.setTimeout(() => {
            loadStep({ step: Step.Position, search });
        }, 0);
        return () => window.clearTimeout(id);
    }, [loadStep, search]);

    if (isPending) return <CustomizationSkeleton />;

    return <PagePositionQuery locationNames={locationNames} takenLocations={takenLocations} />;
});

const selector = (s: AppendState<AppendCartState>) => {
    const { item_ids } = s.customisation;
    const first_item_id = item_ids[0];
    const matchingItem = s.cart.items.find((item) => String(item.id) === String(first_item_id));
    if (!matchingItem) {
        throw new Error('Cannot recover. Matching basket item is not found');
    }
    return {
        summary: s.customisation.summary,
        firstSku: matchingItem.product.sku,
        applicationMethod: s.customisation.application_method,
    };
};

export const PagePositionQuery: React.FC<{ locationNames: string[]; takenLocations: string[] }> = (props) => {
    const { firstSku, summary } = useSelector(selector, shallowEqual);
    const { loading, error, data } = useQuery<getCustomisationLocations, getCustomisationLocationsVariables>(
        getCustomisationLocationsQuery,
        {
            variables: { sku: firstSku },
            ssr: false,
        },
    );

    const locations = useMemo(() => {
        if (!data || !data.products || !data.products.items) {
            return [];
        }
        const first = data.products.items[0];
        if (!first) return [];
        return cleanLocations(
            first.locations as getCustomisationLocations_products_items_locations[],
        ) as ProductLocation[];
    }, [data]);

    const recommendedApplication = useMemo(() => {
        if (!data || !data.products || !data.products.items) {
            return ApplicationMethodAPI.None;
        }
        const first = data.products.items[0];
        if (!first) return ApplicationMethodAPI.None;
        return first.recommended_application;
    }, [data]);
    if (loading) return <CustomizationSkeleton />;
    if (error) return <ErrorOrOfflineConnected message={`Data fetch error ${error}`} />;

    return (
        <Position
            summary={summary}
            locations={locations}
            locationNames={props.locationNames}
            takenLocations={props.takenLocations}
            recommendedApplication={recommendedApplication}
        />
    );
};
